import { Redirect } from 'react-router-dom';

import { LoanStatus } from '@willow/graphql-iso/src/portal';
import { PORTAL_LOAN_PATHS, z } from '@willow/shared-web';
import { PayoffAllowedStatuses } from '@willow/types-iso';

import { PortalSelectedLoan } from '../../App';
import { NavSection } from '../../components/layout/nav-section/NavSection';
import { NavSectionItem } from '../../components/layout/nav-section/NavSectionItem';
import { ManageLoanContact } from '../../components/manage-loan/Contact';
import { DrawRequest } from '../../components/manage-loan/DrawRequest';
import { EscrowWaiverRequest } from '../../components/manage-loan/EscrowWaiverRequest';
import { GeneralInquiry } from '../../components/manage-loan/GeneralInquiry';
import { PaymentAssistanceInquiry } from '../../components/manage-loan/PaymentAssistanceInquiry';
import { PayoffRequestForm } from '../../components/manage-loan/PayoffRequestForm';
import { RequestPMICancellation } from '../../components/manage-loan/RequestPMICancellation';
import { RequestVOMForm } from '../../components/manage-loan/RequestVOMForm';
import { Metadata } from '../../components/metadata/Metadata';
import { PageHeading } from '../../components/page-heading/PageHeading';

export interface Props {
  loan: PortalSelectedLoan;
}

export const ManageLoanPage = ({ loan }: Props) => {
  const {
    company: { portalSettings },
    currentSnapshot: { loanStatus, drawsEnabled, hasMortgageInsurance },
  } = loan;

  const { borrowerRequests } = portalSettings || {};
  const {
    payoffRequestEnabled,
    drawRequestEnabled,
    paymentAssistanceInquiryEnabled,
    escrowWaiverRequestEnabled,
    pmiCancellationRequestEnabled,
    generalInquiryEnabled,
    verificationOfMortgageEnabled,
  } = borrowerRequests || {};

  if ([LoanStatus.Reo, LoanStatus.Liquidated].includes(loan.currentSnapshot.loanStatus)) {
    return <Redirect to={PORTAL_LOAN_PATHS.root({ losId: loan.losId, companyId: loan.company.id })} />;
  }

  return (
    <div>
      <Metadata metadata={{ title: 'Manage Loan', description: 'Assistance for common mortgage requests' }} />

      <PageHeading title="Manage Loan" subtitle="Assistance for common mortgage requests" />

      <NavSection>
        {verificationOfMortgageEnabled && (
          <NavSectionItem key="vom" label="Verification of Mortgage">
            <RequestVOMForm loan={loan} />
          </NavSectionItem>
        )}
        {payoffRequestEnabled && z.validate(PayoffAllowedStatuses, loanStatus) && (
          <NavSectionItem key="payoff" label="Request a Payoff Statement">
            <PayoffRequestForm loan={loan} />
          </NavSectionItem>
        )}

        {drawRequestEnabled && drawsEnabled && (
          <NavSectionItem key="drawRequest" label="Request a Draw">
            <DrawRequest loan={loan} />
          </NavSectionItem>
        )}

        {paymentAssistanceInquiryEnabled && (
          <NavSectionItem key="paymentAssistance" label="Payment Assistance Inquiry">
            <PaymentAssistanceInquiry loan={loan} />
          </NavSectionItem>
        )}

        {escrowWaiverRequestEnabled && (
          <NavSectionItem key="escrowWaiver" label="Request an Escrow Waiver">
            <EscrowWaiverRequest loan={loan} />
          </NavSectionItem>
        )}

        {pmiCancellationRequestEnabled && hasMortgageInsurance && (
          <NavSectionItem key="pmiCancel" label="Request PMI Cancellation">
            <RequestPMICancellation loan={loan} />
          </NavSectionItem>
        )}

        {generalInquiryEnabled && (
          <NavSectionItem key="generalInquiry" label="General Inquiry">
            <GeneralInquiry loan={loan} />
          </NavSectionItem>
        )}

        <NavSectionItem key="contact" label="Contact Us">
          <ManageLoanContact loan={loan} />
        </NavSectionItem>
      </NavSection>
    </div>
  );
};
